import { NextRouter } from 'next/router';
import { NavSection, UiKit, NavItem } from '@datavant/uikit';
import { UserWithPermissions } from './user';
import { RecordHubNavIcons } from '@datavant/idsb-shared/components/AllIcons';
import { DebugFlag } from './user/debugFlags';
import { CustomIcons } from '../components/CustomIcons';
import { PAYER_NAMING } from '../components/PAYER_NAMING';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { Permission } from './permissions';
import { userHasRequestManagerAccess } from './userPermissionChecks';
import { useUserPermissions } from './contexts/PermissionsProviderContext';
import { NavIcons } from '../components/NavIcons';
import { getEnv } from '@datavant/idsb-shared/utils/browserEnvironmentVariables';

const SIDE_NAV_PERMISSIONS = [
    Permission.CAN_CREATE_TPO_PROJECT_TYPES,
    Permission.CAN_VIEW_TPO_PROJECT_TYPES,
    Permission.CAN_CREATE_TPO_PROJECTS,
    Permission.CAN_VIEW_TPO_PROJECTS,
    Permission.CAN_VIEW_ORDERS,
    Permission.CAN_VIEW_ORDER_TEMPLATES,
    Permission.CAN_CREATE_CDE,
    Permission.CAN_VIEW_INVOICE_CONFIGURATION,
    Permission.CAN_VIEW_INVOICE_REPORT,
    Permission.CAN_VIEW_PPT_FEE_SCHEDULES,
    Permission.CAN_UPDATE_CUSTOMER_SETTINGS,
    Permission.CAN_VIEW_REQUEST_MANAGER,
    Permission.CAN_VIEW_USER_MANAGEMENT,
    Permission.CAN_VIEW_ORGANIZATION_INFORMATION,
    Permission.CAN_VIEW_UPLOAD_UNIVERSAL_ROSTER,
    Permission.CAN_VIEW_DF_ROI_EMBEDDED_ROSTER,
    Permission.CAN_VIEW_QA_WORKBENCH,
    Permission.CAN_PERFORM_RETRIEVAL_TASKS,
    Permission.CAN_VIEW_CREATE_PREMATCHES,
    Permission.CAN_VIEW_ACTION_CENTER,
    Permission.CAN_MANAGE_EMBEDDED_RECORD_REQUESTS,
    Permission.CAN_VIEW_EMBEDDED_ADDRESS_DRA_FEEDBACK,
] as const;

type SideNavPermissions = { [K in (typeof SIDE_NAV_PERMISSIONS)[number]]: boolean };

export const requestURLPrefix = 'request';
export const projectURLPrefix = 'project';
export const orderURLPrefix = 'order';
export const chartDetailExtractURLPrefix = 'chart-detail-extract';
export const CREATE_REQUEST_LABEL = 'Create Request';

const tpoRequestorTools = (
    user: UserWithPermissions,
    router: NextRouter,
    debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => {
    const generalRule = (user.isDatavanter && debugFlags.isTpo) || user.isTpoUser === true;

    return [
        {
            label: 'Requests',
            items: [
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Registration,
                    label: `[old flow] Create a ${PAYER_NAMING.Project}`,
                    isActivePage: router.asPath === `/${projectURLPrefix}/create`,
                    path: `/${projectURLPrefix}/create`,
                    displayRule: () => userPermissions[Permission.CAN_CREATE_TPO_PROJECT_TYPES],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: `[old flow] All ${PAYER_NAMING.Projects}`,
                    isActivePage: router.asPath === `/${projectURLPrefix}/all`,
                    path: `/${projectURLPrefix}/all`,
                    displayRule: () => generalRule && userPermissions[Permission.CAN_VIEW_TPO_PROJECT_TYPES],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.AddNewRequest,
                    label: `[old flow] Create a ${PAYER_NAMING.Order}`,
                    isActivePage: router.asPath.startsWith(`/${requestURLPrefix}/create`),
                    path: `/${requestURLPrefix}/create/batch`,
                    displayRule: () => userPermissions[Permission.CAN_CREATE_TPO_PROJECTS],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: `[old flow] All ${PAYER_NAMING.Orders}`,
                    isActivePage: router.asPath === `/${orderURLPrefix}/all`,
                    path: `/${orderURLPrefix}/all`,
                    displayRule: () => userPermissions[Permission.CAN_VIEW_TPO_PROJECTS],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: 'Orders',
                    isActivePage: router.asPath === '/order-management/orders',
                    path: '/order-management/orders',
                    displayRule: () => userPermissions[Permission.CAN_VIEW_ORDERS],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: 'Order Templates',
                    isActivePage: router.asPath === '/order-management/order-templates',
                    path: '/order-management/order-templates',
                    displayRule: () => userPermissions[Permission.CAN_VIEW_ORDER_TEMPLATES],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.InternalReports,
                    label: '[old flow] Create a CDE',
                    isActivePage: router.asPath === `/${chartDetailExtractURLPrefix}/create`,
                    path: `/${chartDetailExtractURLPrefix}/create`,
                    displayRule: () => userPermissions[Permission.CAN_CREATE_CDE],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.InternalReports,
                    label: 'CDEs',
                    isActivePage: router.asPath.startsWith('/cde-management'),
                    path: '/cde-management',
                    displayRule: () => userPermissions[Permission.CAN_CREATE_CDE],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.InternalReports,
                    label: 'PPT Fee Schedules',
                    isActivePage: router.asPath.startsWith('/ppt-fee-schedules'),
                    path: '/ppt-fee-schedules',
                    // Gated on the same permission the list endpoint enforces.
                    displayRule: () => userPermissions[Permission.CAN_VIEW_PPT_FEE_SCHEDULES],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.MySettings,
                    label: 'Customer Settings',
                    isActivePage: router.asPath === `/${requestURLPrefix}/customer-configuration`,
                    path: `/${requestURLPrefix}/customer-configuration`,
                    displayRule: () => userPermissions[Permission.CAN_UPDATE_CUSTOMER_SETTINGS],
                },
            ],
        },
    ];
};

const invoiceTools = (
    _user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    _featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => [
    {
        label: 'Invoices',
        items: [
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.InternalReports,
                label: 'Configurations',
                isActivePage: router.asPath.startsWith('/invoices/configurations'),
                path: '/invoices/configurations',
                displayRule: () => userPermissions[Permission.CAN_VIEW_INVOICE_CONFIGURATION],
            },
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.InternalReports,
                label: 'Reports',
                isActivePage: router.asPath.startsWith('/invoices/reports'),
                path: '/invoices/reports',
                displayRule: () => userPermissions[Permission.CAN_VIEW_INVOICE_REPORT],
            },
        ],
    },
];

const getProviderTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => {
    const { canAccessRequestManager } = userHasRequestManagerAccess(
        user,
        userPermissions[Permission.CAN_VIEW_REQUEST_MANAGER],
        featureFlags['enforceStrictRequestManagerAccess'] ?? false
    );

    return [
        {
            label: 'Provider',
            items: [
                {
                    kind: 'NavItemLink',
                    icon: UiKit.Icons.SVG.SideMenu.Configurations,
                    label: 'Request Manager',
                    isActivePage: router.asPath === '/provider-tools/request-manager',
                    path: '/provider-tools/request-manager',
                    displayRule: () => canAccessRequestManager,
                },
                {
                    kind: 'NavItemLink',
                    icon: UiKit.Icons.SVG.SideMenu.Configurations,
                    label: 'Digital Dashboard',
                    isActivePage: router.asPath === '/provider-tools/digital-fulfillment-dashboard',
                    path: '/provider-tools/digital-fulfillment-dashboard',
                    displayRule: () => false,
                },
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconClock,
                    label: 'File Upload History',
                    isActivePage: router.asPath === '/provider-tools/request-manager/file-upload-history',
                    path: '/provider-tools/request-manager/file-upload-history',
                    displayRule: () => canAccessRequestManager,
                },
            ],
        },
    ];
};

const getSettings = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => {
    if (!user?.tenantUUID) {
        return [];
    }

    const { canAccessRequestManager } = userHasRequestManagerAccess(
        user,
        userPermissions[Permission.CAN_VIEW_REQUEST_MANAGER],
        featureFlags['enforceStrictRequestManagerAccess'] ?? false
    );

    return [
        {
            label: 'Settings',
            items: [
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconUserCircle,
                    label: 'User Management',
                    isActivePage: router.asPath === '/settings/users',
                    path: '/settings/users',
                    displayRule: () =>
                        canAccessRequestManager || userPermissions[Permission.CAN_VIEW_USER_MANAGEMENT],
                },
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconMap,
                    label: 'Sites Addresses',
                    isActivePage: router.asPath === '/settings/sites',
                    path: '/settings/sites',
                    displayRule: () => canAccessRequestManager,
                },
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconDeviceLandlinePhone,
                    label: 'Phone/Fax Minimization',
                    isActivePage: router.asPath === '/settings/outreach',
                    path: '/settings/outreach',
                    displayRule: () => canAccessRequestManager,
                },
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconBadge,
                    label: 'Providers',
                    isActivePage: router.asPath === '/settings/providers',
                    path: '/settings/providers',
                    displayRule: () => canAccessRequestManager,
                },
                {
                    kind: 'NavItemLink',
                    icon: NavIcons.IconBuildings,
                    label: 'Organization Information',
                    isActivePage: router.asPath === '/settings/organizations',
                    path: '/settings/organizations',
                    displayRule: () =>
                        canAccessRequestManager ||
                        userPermissions[Permission.CAN_VIEW_ORGANIZATION_INFORMATION],
                },
            ],
        },
    ];
};

const rosterManagementTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => [
    {
        label: 'Roster Management',
        items: [
            {
                kind: 'NavItemLink',
                // using placeholder icon, will add our own later
                icon: RecordHubNavIcons.AddNewRequest,
                label: 'Universal Roster Uploader',
                isActivePage: router.asPath === '/roster-management/upload-roster',
                path: '/roster-management/upload-roster',
                displayRule: () => userPermissions[Permission.CAN_VIEW_UPLOAD_UNIVERSAL_ROSTER],
            },
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.Requests,
                label: 'ROI Embedded Roster',
                isActivePage: router.asPath === '/roster-management/data-foundation-roi-tables',
                path: '/roster-management/data-foundation-roi-tables',
                displayRule: () => userPermissions[Permission.CAN_VIEW_DF_ROI_EMBEDDED_ROSTER],
            },
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.Requests,
                label: 'Embedded Address Feedback Tool',
                isActivePage: router.asPath === '/roster-management/embedded-address-feedback',
                path: '/roster-management/embedded-address-feedback',
                displayRule: () => userPermissions[Permission.CAN_VIEW_EMBEDDED_ADDRESS_DRA_FEEDBACK],
            },
        ],
    },
];

const taskQueueTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => {
    const env = getEnv();
    return [
        {
            label: 'Task Queues',
            items: [
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: 'QA Workbench',
                    isActivePage: router.asPath === '/task-queues/workbench/qa',
                    path: '/task-queues/workbench/qa',
                    displayRule: () => userPermissions[Permission.CAN_VIEW_QA_WORKBENCH],
                },
                {
                    kind: 'NavItemLink',
                    icon: RecordHubNavIcons.Requests,
                    label: 'Retrieval Workbench',
                    isActivePage: router.asPath.startsWith('/task-queues/retrieval'),
                    path: '/task-queues/retrieval',
                    displayRule: () => userPermissions[Permission.CAN_PERFORM_RETRIEVAL_TASKS],
                },
            ],
        },
    ];
};

const facilityStructureExportTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => [
    {
        label: 'Tools',
        items: [
            {
                kind: 'NavItemLink',
                icon: CustomIcons.siteMap,
                label: 'Pre-Match',
                isActivePage: router.asPath === '/tools/pre-match',
                path: '/tools/pre-match',
                displayRule: () => userPermissions[Permission.CAN_VIEW_CREATE_PREMATCHES],
            },
        ],
    },
];

const retrievalExplainerTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    _featureFlags: LDFlagSet,
    _userPermissions: SideNavPermissions
): NavSection[] => [
    {
        label: 'Tools',
        items: [
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.InternalReports,
                label: 'Retrieval Explainer',
                isActivePage: router.asPath.startsWith('/tools/retrieval-explainer'),
                path: '/tools/retrieval-explainer',
                displayRule: () => user.isTrueDatavanter,
            },
        ],
    },
];

const actionCenterTools = (
    user: UserWithPermissions,
    router: NextRouter,
    _debugFlags: DebugFlag,
    featureFlags: LDFlagSet,
    userPermissions: SideNavPermissions
): NavSection[] => [
    {
        label: 'Tools',
        items: [
            {
                kind: 'NavItemLink',
                icon: RecordHubNavIcons.InternalReports,
                label: 'Action Center',
                isActivePage: router.asPath.startsWith('/tools/action-center'),
                path: '/tools/action-center',
                displayRule: () =>
                    (userPermissions[Permission.CAN_VIEW_ACTION_CENTER] &&
                        !!featureFlags['actionCenterClientPausesCancels']) ||
                    (userPermissions[Permission.CAN_MANAGE_EMBEDDED_RECORD_REQUESTS] &&
                        !!featureFlags['idsbPortalActionCenterManageEmbedded']),
            },
        ],
    },
];

export const useSideNav = (
    router: NextRouter,
    user: UserWithPermissions,
    debugFlags: DebugFlag,
    featureFlags: LDFlagSet
): NavSection[] => {
    const { userPermissions } = useUserPermissions(SIDE_NAV_PERMISSIONS);
    const sideNavMap: Record<string, Record<string, NavItem>> = {};

    const addNavSections = (sections: NavSection[]): void =>
        sections.forEach(section =>
            section.items.forEach(item => {
                const displayNavItem = (item.displayRule ?? (() => false))();
                if (displayNavItem) {
                    const sectionLabel = section.label ?? 'default';
                    const itemSet = sideNavMap[sectionLabel];
                    const itemLabel = item.label;
                    if (itemSet) {
                        sideNavMap[sectionLabel] = { ...itemSet, [itemLabel]: item };
                    } else {
                        sideNavMap[sectionLabel] = { [itemLabel]: item };
                    }
                }
            })
        );

    if (!user) {
        return [];
    }

    const allGetToolsMethods = [
        rosterManagementTools,
        taskQueueTools,
        facilityStructureExportTools,
        actionCenterTools,
        retrievalExplainerTools,
        tpoRequestorTools,
        invoiceTools,
        getProviderTools,
        getSettings,
    ];

    allGetToolsMethods.forEach(getTools => {
        addNavSections(getTools(user, router, debugFlags, featureFlags, userPermissions));
    });

    return Object.keys(sideNavMap).map(sectionLabel => ({
        items: Object.values(sideNavMap[sectionLabel]),
        label: sectionLabel === 'default' ? undefined : sectionLabel,
    }));
};
